/**
 * Filter TypeScript types for the Universal CRM Table system
 *
 * This file contains types related to filtering, sorting, pagination, and view state.
 */

import type { FieldValue } from "./base-types";
import type { SortDirection } from "./field-config-types";

// ============================================================================
// FILTER TYPES
// ============================================================================

/**
 * Simple table filters - maps field IDs to filter values
 */
export interface TableFilters {
  [fieldId: string]: string | number | boolean | string[] | null | undefined;
}

/**
 * Advanced filter condition for complex filtering
 */
export interface AdvancedFilterCondition {
  field: string;
  operator: string;
  value: FieldValue;
}

/**
 * Advanced filters - maps field IDs to complex filter configurations
 */
export interface AdvancedFilters {
  [fieldId: string]: {
    operator: "AND" | "OR";
    conditions: AdvancedFilterCondition[];
  };
}

/**
 * Sort state stored in a saved view
 */
export interface SerializedSortState {
  field: string;
  direction: SortDirection;
}

/**
 * Canonical shape for serialized table state: search, filters, advanced filters,
 * sort, visible columns, etc. This is what we serialize, store, and apply.
 */
export interface SerializedViewState {
  search?: string;
  filters?: TableFilters;
  advancedFilters?: AdvancedFilters;
  filterOperator?: "AND" | "OR";
  quickFilters?: Record<string, unknown>;
  sort?: SerializedSortState;
  visibleColumns?: string[];
  columnWidths?: Record<string, number>;
  columnOrder?: string[];
}

/**
 * Filters stored in or applied from a saved view (API + config) - legacy union
 */
export type SavedViewFilters = TableFilters | AdvancedFilters;

// ============================================================================
// PAGINATION TYPES
// ============================================================================

/**
 * Canonical pagination state - unified across EntityPage and CRM table
 * Supports both cursor-based (GraphQL) and offset-based (REST) pagination
 */
export interface PaginationState {
  page: number;
  pageSize: number;
  cursor: string | null;
  totalCount?: number;
  pageInfo?: {
    endCursor?: string | null;
    hasNextPage?: boolean;
    startCursor?: string | null;
    hasPreviousPage?: boolean;
  };
}

// ============================================================================
// FILTER TYPE GUARDS AND NORMALIZATION
// ============================================================================

/**
 * Type guard: check if value is a SerializedViewState
 */
export function isSerializedViewState(x: unknown): x is SerializedViewState {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return (
    "search" in o ||
    "sort" in o ||
    "visibleColumns" in o ||
    "advancedFilters" in o ||
    ("filters" in o && typeof o.filters === "object")
  );
}

/**
 * Type guard: check if value is AdvancedFilters
 */
export function isAdvancedFilters(x: unknown): x is AdvancedFilters {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  const values = Object.values(o);
  if (values.length === 0) return false;
  const v = values[0];
  return typeof v === "object" && v !== null && "operator" in v && "conditions" in v;
}

/**
 * Normalize various filter formats to canonical SerializedViewState
 */
export function normalizeToSerializedViewState(
  raw: SerializedViewState | TableFilters | AdvancedFilters | null | undefined
): SerializedViewState {
  if (!raw) return {};
  if (isSerializedViewState(raw)) return raw as SerializedViewState;
  if (isAdvancedFilters(raw)) return { advancedFilters: raw };
  return { filters: raw as TableFilters };
}

/**
 * Normalize filters to ensure arrays are preserved and values are properly typed
 * This is the centralized function for all filter normalization
 *
 * Handles conversion of comma-separated strings to arrays for multi-value fields
 * (e.g., "prospect,customer" → ["prospect", "customer"])
 */
export function normalizeFilters(filters: Record<string, any>): TableFilters {
  const normalized: TableFilters = {};

  Object.entries(filters).forEach(([key, value]) => {
    // Skip null, undefined, or empty string values
    if (value === null || value === undefined || value === "") {
      return;
    }

    // Preserve arrays as-is (don't convert to comma-separated strings)
    if (Array.isArray(value)) {
      normalized[key] = value as string[];
    } else if (typeof value === "string") {
      // Convert comma-separated strings to arrays for multi-value fields
      // This handles saved views that store "prospect,customer" as a string
      if (value.includes(",")) {
        normalized[key] = value
          .split(",")
          .map((v) => v.trim())
          .filter((v) => v !== "");
      } else {
        normalized[key] = value;
      }
    } else if (typeof value === "number" || typeof value === "boolean") {
      normalized[key] = value;
    }
  });

  return normalized;
}
